// ============================================================================
// ListlogistLogo — фирменный логотип. Brand Book v2.0.
// ============================================================================
// ЗНАК — рамка-видоискатель: четыре скруглённые угловые скобки и вертикальный
// курсор внутри. Смысл: «мы наводим фокус на нужную сделку».
// Сетка 48, обводка 3, скруглённые концы и стыки.
//
// ВОРДМАРК — «list logist»: СТРОЧНЫМИ, В ДВА СЛОВА. Не «Listlogist».
// Это главный элемент айдентики, писать его иначе нельзя.
//
// Монохром: цвет берётся из currentColor. Знак не наклоняется, не растягивается,
// не перекрашивается во второй цвет, не получает теней (см. «Так нельзя»).
//
//   <ListlogistLogo />                    знак + вордмарк
//   <ListlogistLogo variant="mark" />     только знак
//   <ListlogistLogo variant="wordmark" /> только вордмарк
// ============================================================================

import clsx from 'clsx'

type Variant = 'full' | 'mark' | 'wordmark'
type Size = 'sm' | 'md' | 'lg' | 'xl'
type Tone = 'auto' | 'light' | 'dark'

interface Props {
  variant?: Variant
  size?: Size
  tone?: Tone
  /** Не используется (логотип монохромный); оставлено для совместимости API. */
  accent?: string
  className?: string
  ariaLabel?: string
}

const MARK_PX: Record<Size, number> = { sm: 20, md: 28, lg: 36, xl: 52 }
const WORD_PX: Record<Size, string> = {
  sm: 'text-[17px]', md: 'text-[22px]', lg: 'text-[29px]', xl: 'text-[42px]',
}
const GAP: Record<Size, string> = { sm: 'gap-1.5', md: 'gap-2', lg: 'gap-2.5', xl: 'gap-3' }

function toneClass(tone: Tone): string {
  if (tone === 'dark') return 'text-white'
  if (tone === 'light') return 'text-ink-900'
  return 'text-ink-900 dark:text-white'
}

/** Знак: рамка-видоискатель с курсором. Векторный, тянется за currentColor. */
function Mark({ px, className }: { px: number; className?: string }) {
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <g
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 20V17A5 5 0 0 1 17 12H20" />
        <path d="M28 12H31A5 5 0 0 1 36 17V20" />
        <path d="M36 28V31A5 5 0 0 1 31 36H28" />
        <path d="M20 36H17A5 5 0 0 1 12 31V28" />
      </g>
      <rect x="19" y="17" width="3" height="14" rx="1.5" fill="currentColor" />
    </svg>
  )
}

/**
 * Вордмарк. Набирается ТЕКСТОМ, а не кривыми: так он остаётся чётким на любом
 * экране и масштабируется без потерь. Гарнитура — Onest (гарнитура бренда),
 * с откатом на системный гротеск, если шрифт ещё не загрузился.
 */
function Wordmark({ size, className }: { size: Size; className?: string }) {
  return (
    <span
      className={clsx(
        WORD_PX[size],
        'font-extrabold tracking-tight leading-none whitespace-nowrap',
        className,
      )}
      style={{ fontFamily: 'Onest, Manrope, system-ui, sans-serif' }}
    >
      list logist
    </span>
  )
}

export function ListlogistLogo({
  variant = 'full',
  size = 'md',
  tone = 'auto',
  className,
  ariaLabel = 'list logist',
}: Props) {
  const color = toneClass(tone)

  if (variant === 'mark') {
    return (
      <span className={clsx('inline-flex', color, className)} aria-label={ariaLabel} role="img">
        <Mark px={MARK_PX[size]} />
      </span>
    )
  }

  if (variant === 'wordmark') {
    return (
      <span className={clsx('inline-flex items-center', color, className)} aria-label={ariaLabel} role="img">
        <Wordmark size={size} />
      </span>
    )
  }

  return (
    <span
      className={clsx('inline-flex items-center', GAP[size], color, className)}
      aria-label={ariaLabel}
      role="img"
    >
      <Mark px={MARK_PX[size]} />
      <Wordmark size={size} />
    </span>
  )
}

export default ListlogistLogo
